import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldAlt, faStar, faUsers, faCode, faArrowUp, faFilter } from "@fortawesome/pro-duotone-svg-icons";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";

import styles from "./styles/QuickActions.module.scss";
import { isV2GacEnabled } from "../../utils/v2gac";
import { isV2TWEnabled } from "../../utils/v2tw";
import { isV2TBEnabled } from "../../utils/v2tb";

// Live status per card (next GAC round, TW/TB event state) is not wired up yet — each one is a full
// feature-page fetch (GACAPI.get / TWAPI.get / BaseAPI.fetchTbGameData), not a lightweight status check, so
// pulling all three into the dashboard on every load is a separate piece of work. Tracked in handoff.md.
//
// Cards are fixed-size tiles, not stretched grid columns — deliberately, so the row has slack for more
// shortcuts later, and so a future editable/reorderable grid has uniform tiles to work with. Mods is
// intentionally NOT one of these uniform tiles — it's its own two-link panel, by design.
function QuickActionCard(props: { to: string; icon: any; title: string; description: string }) {
    return (
        <Link to={props.to} className={styles.card}>
            <span className={styles.cardIcon}>
                <FontAwesomeIcon icon={props.icon} />
            </span>
            <span className={styles.cardTitle}>{props.title}</span>
            <span className={styles.cardDescription}>{props.description}</span>
        </Link>
    );
}

export default function QuickActions() {
    return (
        <div className={styles.grid}>
            {/*
                Each card points at the V2 app where that app is available, and falls back to the legacy
                page where it is not (owner, 2026-08-27: repoint these three at the new tools).

                🔴 The fallback is not politeness, it is required. Every `isV2*Enabled()` is TRUE ONLY on
                development.hotutils.com and localhost — and the matching route in App.tsx carries the
                SAME gate, so on hotutils.com `/gac/v2` is not a route at all. A hard-coded V2 target
                would land the live site's most prominent shortcut on the 404 page, and it would look
                fine in dev right up until it shipped. When a V2 app graduates to prod its gate flips and
                these follow with it — nothing to remember here.
            */}
            <QuickActionCard to={isV2GacEnabled() ? "/gac/v2" : "/gac/current"} icon={faStar} title="GAC"
                              description="Check GAC status and matchups"/>
            <QuickActionCard to={isV2TWEnabled() ? "/tw/v2" : "/tw/current"} icon={faUsers} title="Territory Wars"
                              description="Check TW status and orders"/>
            <QuickActionCard to={isV2TBEnabled() ? "/tb/v2" : "/tb/status"} icon={faShieldAlt} title="Territory Battles"
                              description="Check TB status and orders"/>

            <div className={styles.modsCard}>
                <span className={styles.modsTitle}>
                    <FontAwesomeIcon icon={faCode}/> Mods
                </span>
                <Link to="/mods/upgrade" className={styles.modsLink}>
                    <FontAwesomeIcon icon={faArrowUp} className={styles.modsLinkIcon}/>
                    <span>
                        <span className={styles.modsLinkTitle}>Upgrade</span>
                        <span className={styles.modsLinkSub}>Guided wizard</span>
                    </span>
                    <FontAwesomeIcon icon={faChevronRight} className={styles.modsLinkChevron}/>
                </Link>
                <Link to="/mods/management" className={styles.modsLink}>
                    <FontAwesomeIcon icon={faFilter} className={styles.modsLinkIcon}/>
                    <span>
                        <span className={styles.modsLinkTitle}>Management</span>
                        <span className={styles.modsLinkSub}>Advanced filters</span>
                    </span>
                    <FontAwesomeIcon icon={faChevronRight} className={styles.modsLinkChevron}/>
                </Link>
            </div>
        </div>
    );
}
